'use client'

import { analytics } from '@/lib/analytics'
import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import {
  useSessionAwareConnectedIntegrationsQuery,
  useSessionAwareOnboardingQuery,
} from '@/lib/features/user/userApi'
import { connectGoogleSearchConsoleOAuthIntegration } from '@/lib/google-search-console-oauth-auth'
import { getIntegrationConfig } from '@/lib/integrations'
import { connectPipedreamIntegration } from '@/lib/pipedream-auth'
import { initiateWebflowOAuth } from '@/lib/webflow-oauth-auth'
import { CheckCircle2, Edit, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { IntegrationType } from 'sprites-types/api/v2/integrations'
import Modal from '../Modal/Modal'
import { Button } from '../ui/button'
import { Spinner } from '../ui/spinner'
import { ApiCredentialsModal } from './ApiCredentialsModal'
import { FacebookOAuthModal } from './FacebookOAuthModal'
import { GoogleAdsOAuthModal } from './GoogleAdsOAuthModal'

type AuthType = 'credentials' | 'oauth'

type Integration = {
  id: string
  name: string
  provider: IntegrationType
  authType: AuthType
  connected: boolean
  credentials?: Record<string, string>
}

type IntegrationsModalProps = {
  isOpen: boolean
  onClose: () => void
}

const AVAILABLE_INTEGRATIONS: Record<
  IntegrationType,
  {
    name: string
    description: string
    authType: AuthType
  }
> = {
  facebook: {
    name: 'Meta Ads',
    description: 'Access ads performance data',
    authType: 'oauth',
  },
  googleAds: {
    name: 'Google Ads',
    description: 'Access campaign performance and keyword data',
    authType: 'oauth',
  },
  googleSearchConsole: {
    name: 'Google Search Console',
    description: 'Inspect URLs for indexing issues and get search performance data',
    authType: 'oauth',
  },
  shopify: {
    name: 'Shopify',
    description: 'Manage blog articles, pages, and products',
    authType: 'credentials',
  },
  wordpress: {
    name: 'WordPress',
    description: 'Publish and update pages on your WordPress site',
    authType: 'credentials',
  },
  gmail: {
    name: 'Gmail',
    description: 'Send and manage emails through your Gmail account',
    authType: 'oauth',
  },
  google_drive: {
    name: 'Google Drive',
    description: 'Upload, download, and manage files in Google Drive',
    authType: 'oauth',
  },
  linkedin: {
    name: 'LinkedIn',
    description: 'Create posts and manage your LinkedIn presence',
    authType: 'oauth',
  },
  webflow: {
    name: 'Webflow',
    description: 'Publish and manage content on your Webflow sites',
    authType: 'oauth',
  },
  framer: {
    name: 'Framer',
    description: 'Connect to Framer via temporary connection URL',
    authType: 'credentials',
  },
} satisfies Record<IntegrationType, { name: string; description: string; authType: AuthType }>

const getIntegrationKeys = () => Object.keys(AVAILABLE_INTEGRATIONS) as IntegrationType[]

export function IntegrationsModal({ isOpen, onClose }: IntegrationsModalProps) {
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [deletingProvider, setDeletingProvider] = useState<string | null>(null)
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null)
  const currentBusinessProfileId = useCurrentBusinessProfileId()
  const { data: connectedIntegrations } = useSessionAwareConnectedIntegrationsQuery(
    currentBusinessProfileId || undefined
  )
  const { data: onboardingData } = useSessionAwareOnboardingQuery()

  const [showAddModal, setShowAddModal] = useState<'wordpress' | 'framer' | 'shopify' | null>(null)
  const [editingProvider, setEditingProvider] = useState<'wordpress' | 'framer' | 'shopify' | null>(
    null
  )
  const [showFacebookModal, setShowFacebookModal] = useState(false)
  const [facebookEditMode, setFacebookEditMode] = useState(false)
  const [showGoogleAdsModal, setShowGoogleAdsModal] = useState(false)
  const [googleAdsEditMode, setGoogleAdsEditMode] = useState(false)
  useEffect(() => {
    if (isOpen) {
      loadIntegrations()
    }
  }, [isOpen])

  // Reload integrations when business profile changes
  useEffect(() => {
    if (isOpen && currentBusinessProfileId) {
      loadIntegrations()
    }
  }, [currentBusinessProfileId, isOpen])

  const loadIntegrations = async () => {
    setIsLoading(true)
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )

      if (response.ok) {
        const result = await response.json()
        const connectedProviders = new Set(result.providerIds || [])

        const integrationsList: Integration[] = getIntegrationKeys().map((provider) => {
          const config = AVAILABLE_INTEGRATIONS[provider]
          return {
            id: provider,
            name: config.name,
            provider,
            authType: config.authType,
            connected: connectedProviders.has(provider),
          }
        })

        setIntegrations(integrationsList)
      } else {
        toast.error('Failed to load integrations')
      }
    } catch (error) {
      console.error('Error loading integrations:', error)
      toast.error('Failed to load integrations')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (provider: string) => {
    setDeletingProvider(provider)
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/${provider}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      )

      if (response.ok) {
        toast.success('Integration removed')
        loadIntegrations()
      } else {
        toast.error('Failed to remove integration')
      }
    } catch (error) {
      console.error('Error deleting integration:', error)
      toast.error('Failed to remove integration')
    } finally {
      setDeletingProvider(null)
    }
  }

  const handleOAuthConnect = async (provider: IntegrationType) => {
    setConnectingProvider(provider)
    try {
      // Google Search Console uses its own OAuth flow
      if (provider === 'googleSearchConsole') {
        const success = await connectGoogleSearchConsoleOAuthIntegration()

        if (!success) {
          toast.error('Failed to connect to Google Search Console')
          return
        }

        // Check if this is the first integration (activation)
        const isFirstIntegration =
          !connectedIntegrations?.providerIds || connectedIntegrations.providerIds.length === 0

        // Track integration connection from menu
        analytics.copilot.integrationConnected('googleSearchConsole', 'menu')

        // Track activation if this is the first integration
        if (isFirstIntegration && onboardingData?.onboardingCompleted) {
          const onboardingCompletedAt = onboardingData.updatedAt
            ? new Date(onboardingData.updatedAt).getTime()
            : null
          const timeSinceOnboarding = onboardingCompletedAt
            ? Math.round((Date.now() - onboardingCompletedAt) / 1000)
            : 0
          analytics.activation.firstIntegrationConnected(
            'googleSearchConsole',
            'menu',
            timeSinceOnboarding
          )
        }

        toast.success('Successfully connected to Google Search Console')
        void loadIntegrations()
        return
      }

      // Webflow uses its own OAuth flow
      if (provider === 'webflow') {
        const result = await initiateWebflowOAuth()

        if (!result.success) {
          toast.error(result.error || 'Failed to connect to Webflow')
          return
        }

        // Check if this is the first integration (activation)
        const isFirstIntegration =
          !connectedIntegrations?.providerIds || connectedIntegrations.providerIds.length === 0

        // Track integration connection from menu
        analytics.copilot.integrationConnected('webflow', 'menu')

        // Track activation if this is the first integration
        if (isFirstIntegration && onboardingData?.onboardingCompleted) {
          const onboardingCompletedAt = onboardingData.updatedAt
            ? new Date(onboardingData.updatedAt).getTime()
            : null
          const timeSinceOnboarding = onboardingCompletedAt
            ? Math.round((Date.now() - onboardingCompletedAt) / 1000)
            : 0
          analytics.activation.firstIntegrationConnected('webflow', 'menu', timeSinceOnboarding)
        }

        toast.success('Successfully connected to Webflow')
        void loadIntegrations()
        return
      }

      // Other providers use Pipedream
      const accountId = await connectPipedreamIntegration(provider)

      if (!accountId) {
        toast.error(`Failed to connect to ${provider}`)
        return
      }

      // Save OAuth tokens to database
      const saveResponse = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/pipedream/saveTokens`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accountId,
            appSlug: provider,
          }),
        }
      )

      if (!saveResponse.ok) {
        console.error('Failed to save OAuth tokens')
        toast.error('Connected but failed to save credentials')
        return
      }

      // Check if this is the first integration (activation)
      const isFirstIntegration =
        !connectedIntegrations?.providerIds || connectedIntegrations.providerIds.length === 0

      // Track integration connection from menu
      analytics.copilot.integrationConnected(provider, 'menu')

      // Track activation if this is the first integration
      if (isFirstIntegration && onboardingData?.onboardingCompleted) {
        const onboardingCompletedAt = onboardingData.updatedAt
          ? new Date(onboardingData.updatedAt).getTime()
          : null
        const timeSinceOnboarding = onboardingCompletedAt
          ? Math.round((Date.now() - onboardingCompletedAt) / 1000)
          : 0
        analytics.activation.firstIntegrationConnected(provider, 'menu', timeSinceOnboarding)
      }

      toast.success(`Successfully connected to ${AVAILABLE_INTEGRATIONS[provider].name}`)
      void loadIntegrations()
    } catch (error) {
      console.error('Error connecting OAuth integration:', error)
      toast.error('Failed to connect integration')
    } finally {
      setConnectingProvider(null)
    }
  }

  const handleAddSuccess = () => {
    setShowAddModal(null)
    loadIntegrations()
  }

  const handleEditSuccess = () => {
    setEditingProvider(null)
    loadIntegrations()
  }

  const handleConnect = (integration: Integration) => {
    if (integration.provider === 'facebook') {
      setFacebookEditMode(false)
      setShowFacebookModal(true)
    } else if (integration.provider === 'googleAds') {
      setGoogleAdsEditMode(false)
      setShowGoogleAdsModal(true)
    } else if (integration.authType === 'credentials') {
      setShowAddModal(integration.provider as 'wordpress' | 'framer' | 'shopify')
    } else {
      void handleOAuthConnect(integration.provider)
    }
  }

  const handleEdit = (provider: 'wordpress' | 'framer' | 'shopify' | 'facebook' | 'googleAds') => {
    if (provider === 'facebook') {
      setFacebookEditMode(true)
      setShowFacebookModal(true)
    } else if (provider === 'googleAds') {
      setGoogleAdsEditMode(true)
      setShowGoogleAdsModal(true)
    } else {
      setEditingProvider(provider as 'wordpress' | 'framer' | 'shopify')
    }
  }

  const handleFacebookSuccess = () => {
    // Track integration connection from menu (Facebook)
    if (!facebookEditMode) {
      analytics.copilot.integrationConnected('facebook', 'menu')
    }
    loadIntegrations()
  }

  const handleFacebookClose = () => {
    setShowFacebookModal(false)
    setFacebookEditMode(false)
  }

  const handleGoogleAdsSuccess = () => {
    // Track integration connection from menu (Google Ads)
    if (!googleAdsEditMode) {
      analytics.copilot.integrationConnected('googleAds', 'menu')
    }
    loadIntegrations()
  }

  const handleGoogleAdsClose = () => {
    setShowGoogleAdsModal(false)
    setGoogleAdsEditMode(false)
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="font-hanken-grotesk flex h-full max-h-[90vh] w-full flex-col p-4 sm:p-6">
          <div className="flex h-full min-h-0 flex-col rounded-3xl bg-white p-4 text-black shadow-xl sm:p-6">
            <h2 className="mb-2 text-2xl font-semibold text-black">Integrations</h2>
            <p className="mb-4 text-base text-black/70">
              Connect your accounts to use them in workflows
            </p>

            {isLoading ? (
              <div className="flex min-h-[300px] items-center justify-center">
                <Spinner size={32} />
              </div>
            ) : (
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-2">
                {integrations.map((integration) => {
                  const config = AVAILABLE_INTEGRATIONS[integration.provider]
                  const integrationConfig = getIntegrationConfig(integration.provider)

                  return (
                    <div
                      key={integration.id}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200/50 bg-white/50 p-4 shadow-sm"
                    >
                      <div className="flex flex-1 items-center gap-4">
                        {integrationConfig && (
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-lg ${integrationConfig.iconBgColor}`}
                          >
                            <integrationConfig.icon
                              className={`h-6 w-6 ${integrationConfig.shouldColorIcon ? integrationConfig.iconColor : ''}`}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-black">
                              {integration.name}
                            </h3>
                            {integration.connected && (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            )}
                          </div>
                          <p className="mt-1 text-sm text-black/60">{config.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {integration.connected ? (
                          <>
                            {(integration.authType === 'credentials' ||
                              integration.provider === 'facebook' ||
                              integration.provider === 'googleAds') && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                  handleEdit(
                                    integration.provider as
                                      | 'wordpress'
                                      | 'framer'
                                      | 'shopify'
                                      | 'facebook'
                                      | 'googleAds'
                                  )
                                }
                              >
                                <Edit className="h-4 w-4" />
                                <span className="ml-2">Edit</span>
                              </Button>
                            )}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(integration.provider)}
                              disabled={deletingProvider === integration.provider}
                            >
                              {deletingProvider === integration.provider ? (
                                <>
                                  <Spinner size={16} />
                                  <span className="ml-2">Removing...</span>
                                </>
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4" />
                                  <span className="ml-2">Remove</span>
                                </>
                              )}
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleConnect(integration)}
                            disabled={connectingProvider === integration.provider}
                          >
                            {connectingProvider === integration.provider ? (
                              <>
                                <Spinner size={16} />
                                <span className="ml-2">Connecting...</span>
                              </>
                            ) : (
                              <>
                                <Plus className="h-4 w-4" />
                                <span className="ml-2">Connect</span>
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="mt-4 flex flex-shrink-0 justify-end">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {showAddModal && (
        <ApiCredentialsModal
          isOpen={true}
          appSlug={showAddModal}
          onClose={() => setShowAddModal(null)}
          onSuccess={handleAddSuccess}
          source="menu"
        />
      )}

      {editingProvider && (
        <ApiCredentialsModal
          isOpen={true}
          appSlug={editingProvider}
          editMode={true}
          onClose={() => setEditingProvider(null)}
          onSuccess={handleEditSuccess}
          source="menu"
        />
      )}

      <FacebookOAuthModal
        isOpen={showFacebookModal}
        onClose={handleFacebookClose}
        onSuccess={handleFacebookSuccess}
        editMode={facebookEditMode}
      />

      <GoogleAdsOAuthModal
        isOpen={showGoogleAdsModal}
        onClose={handleGoogleAdsClose}
        onSuccess={handleGoogleAdsSuccess}
        editMode={googleAdsEditMode}
      />
    </>
  )
}
